import React, { useEffect } from 'react';
import { Alert, Col, Row } from 'reactstrap';
import GeneralInfoSection from '../components/room/GeneralInfoSection';
import RequirementTable from '../components/room/RequirementTable';
import BidSpecTable from '../components/room/BidSpecTable';
import LineSelector from '../components/room/LineSelector';
import AdjustPanel from '../components/room/AdjustPanel';
import NegotiationRoundHistory from '../components/room/NegotiationRoundHistory';
import CollaborationChatLog from '../components/room/CollaborationChatLog';
import useNegotiationChat from '../hooks/useNegotiationChat';
import { priceUnitSuffix } from '../utils/formatters';
import { t } from '../utils/negotiationCopy';
import {
  isCargoExpired,
  PARTNER_STATUS,
  pickDefaultLine,
} from '../utils/negotiationStatus';

const PARTNER_WITHDRAWN_TEXT = {
  id: 'Mitra ini sudah keluar dari negosiasi.',
  en: 'You have withdrawn, or were withdrawn, from this negotiation.',
};
// Closed states of the picked alpha line (be#455: status is per line).
const LINE_CLOSED_TEXT = {
  [PARTNER_STATUS.WITHDRAWN]: {
    id: 'Baris ini sudah ditutup karena mitra keluar dari negosiasi.',
    en: 'This line was closed when the partner withdrew from the negotiation.',
  },
  [PARTNER_STATUS.OVERDUE]: {
    id: 'Batas waktu terlewati sebelum harga baris ini disepakati. Perpanjang deadline cargo untuk melanjutkan.',
    en: 'The deadline passed before a price was agreed on this line. Pertamina can extend the deadline.',
  },
};
const EXPIRED_TEXT = {
  id: 'Timer habis. Aksi dikunci; riwayat dan chat tetap bisa dilihat.',
  en: 'Timer expired. Actions are locked; you can still review history and chat.',
};

// One room = one partner of one cargo: its alpha lines (rounds per line) and
// one chat thread. `selectedLineId` lives in the page so the pick survives
// leaving and reopening the room.
const NegotiationRoom = ({
  cargo,
  partner,
  procurement,
  mode = 'buyer',
  nowMs,
  selectedLineId,
  onSelectLine,
  onSubmitRound,
  onAccept,
  onWithdrawRound,
  onWithdraw,
  onRefresh,
}) => {
  const unit = priceUnitSuffix(procurement?.productName);
  const chat = useNegotiationChat(partner?.id, {
    onSystemEvent: () => onRefresh?.({ silent: true }),
  });

  const lines = partner?.lines || [];
  const line =
    lines.find(l => String(l.id) === String(selectedLineId)) ||
    pickDefaultLine(lines, mode);

  // Pin the default once, so it does not jump when the turn changes.
  useEffect(() => {
    if (line && String(line.id) !== String(selectedLineId)) {
      onSelectLine?.(line.id);
    }
  }, [line, selectedLineId, onSelectLine]);

  // After an action the BE writes a system message; pull it right away.
  const withChat =
    fn =>
    async (...args) => {
      await fn(...args);
      chat.refresh().catch(() => {});
    };

  const partnerWithdrawn = partner?.statusLabel === PARTNER_STATUS.WITHDRAWN;
  const closedText = partnerWithdrawn
    ? PARTNER_WITHDRAWN_TEXT
    : LINE_CLOSED_TEXT[line?.statusLabel];
  const expired =
    line?.statusLabel === PARTNER_STATUS.ACTIVE && isCargoExpired(cargo, nowMs);

  return (
    <div>
      <GeneralInfoSection
        cargo={cargo}
        partner={partner}
        procurement={procurement}
      />
      <RequirementTable cargo={cargo} procurement={procurement} />
      <BidSpecTable partner={partner} procurement={procurement} unit={unit} />

      {closedText && (
        <Alert color='warning' className='mb-3'>
          {t(closedText, mode)}
        </Alert>
      )}

      {expired && (
        <Alert color='danger' className='mb-3'>
          {t(EXPIRED_TEXT, mode)}
        </Alert>
      )}

      <Row className='g-3 align-items-start mb-1'>
        <Col lg={7}>
          {lines.length > 1 && (
            <LineSelector
              lines={lines}
              selectedId={line?.id}
              onSelect={onSelectLine}
              unit={unit}
            />
          )}
          {line ? (
            <>
              <AdjustPanel
                key={`adjust-${line.id}`}
                cargo={cargo}
                partner={partner}
                line={line}
                mode={mode}
                nowMs={nowMs}
                unit={unit}
                onSubmitRound={withChat(onSubmitRound)}
                onAccept={withChat(onAccept)}
                onWithdrawRound={withChat(onWithdrawRound)}
                onWithdraw={withChat(onWithdraw)}
              />
              <NegotiationRoundHistory
                key={`history-${line.id}`}
                line={line}
                unit={unit}
                mode={mode}
              />
            </>
          ) : (
            <Alert color='secondary' className='mb-3'>
              This partner has no alpha lines to negotiate.
            </Alert>
          )}
        </Col>
        <Col lg={5}>
          <CollaborationChatLog
            messages={chat.messages}
            onSend={chat.send}
            mode={mode}
          />
        </Col>
      </Row>
    </div>
  );
};

export default NegotiationRoom;
